import * as Y from 'yjs';

/**
 * Shallow-equal check for plain objects (one level deep).
 * Returns true when both values are structurally identical.
 * Used by diffUpdate to avoid rewriting unchanged nested objects
 * like `style` and `props` that callers recreate via spread.
 */
function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) || Array.isArray(b)) return a === b;

  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (aObj[key] !== bObj[key]) return false;
  }
  return true;
}

/**
 * Transparent adapter that wraps a Y.Map of shapes and provides the same
 * API as a raw Y.Map<unknown> but internally stores each shape as a nested
 * Y.Map. On writes it diffs the incoming plain object against a cached
 * snapshot so only actually-changed top-level keys produce Yjs CRDT
 * operations.
 *
 * Diffing uses shallow-equal for object values (style, props) so that
 * spreads like `{ ...existing, x: 100 }` correctly skip unchanged nested
 * objects even though they have new references.
 *
 * Every `get()` returns a **new object** so that downstream consumers
 * relying on reference-based change detection (e.g. Zustand subscriptions)
 * see a new reference whenever the underlying data changes.
 *
 *   const shape = adapter.get(id);
 *   adapter.set(id, { ...shape, x: 100 });   // only writes 'x'
 */
export class ShapeMapAdapter {
  private readonly inner: Y.Map<Y.Map<unknown> | unknown>;
  /** Cache stores a snapshot per shape for diff comparison only.
   *  `get()` always returns a fresh copy — the cache is never exposed. */
  private readonly cache = new Map<string, Record<string, any>>();

  constructor(yjsMap: Y.Map<any>) {
    this.inner = yjsMap as Y.Map<Y.Map<unknown> | unknown>;
  }

  get doc(): Y.Doc | null {
    return this.inner.doc;
  }

  get size(): number {
    return this.inner.size;
  }

  get(id: string): Record<string, any> | undefined {
    const raw = this.inner.get(id);
    if (raw == null) return undefined;

    if (raw instanceof Y.Map) {
      return this.snapshotFromYMap(id, raw);
    }
    return raw as Record<string, any>;
  }

  set(id: string, value: Record<string, any>): void {
    const existing = this.inner.get(id);

    if (existing instanceof Y.Map) {
      this.diffUpdate(id, existing, value);
      return;
    }

    const ymap = new Y.Map<unknown>();
    for (const [k, v] of Object.entries(value)) {
      ymap.set(k, v);
    }
    this.inner.set(id, ymap);
    this.cache.delete(id);
  }

  /**
   * Efficient partial update — only sets the provided keys.
   * Use when you know exactly which fields changed.
   */
  patch(id: string, changes: Record<string, any>): void {
    const existing = this.inner.get(id);
    if (!existing) return;

    if (existing instanceof Y.Map) {
      for (const [k, v] of Object.entries(changes)) {
        if (v === undefined) {
          existing.delete(k);
        } else {
          existing.set(k, v);
        }
      }
      this.cache.delete(id);
      return;
    }

    const merged = { ...(existing as Record<string, any>) };
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined) delete merged[key];
      else merged[key] = value;
    }
    this.inner.set(id, merged);
    this.cache.delete(id);
  }

  delete(id: string): void {
    this.inner.delete(id);
    this.cache.delete(id);
  }

  has(id: string): boolean {
    return this.inner.has(id);
  }

  forEach(fn: (value: Record<string, any>, key: string) => void): void {
    this.inner.forEach((raw, key) => {
      if (raw instanceof Y.Map) {
        fn(this.snapshotFromYMap(key, raw), key);
      } else if (raw != null) {
        fn(raw as Record<string, any>, key);
      }
    });
  }

  keys(): IterableIterator<string> {
    return this.inner.keys();
  }

  values(): Array<Record<string, any>> {
    const result: Array<Record<string, any>> = [];
    this.forEach((v) => result.push(v));
    return result;
  }

  entries(): Array<[string, Record<string, any>]> {
    const result: Array<[string, Record<string, any>]> = [];
    this.forEach((v, k) => result.push([k, v]));
    return result;
  }

  toJSON(): Record<string, Record<string, any>> {
    const result: Record<string, Record<string, any>> = {};
    this.forEach((v, k) => { result[k] = v; });
    return result;
  }

  observe(fn: (event: Y.YMapEvent<any>, transaction: Y.Transaction) => void): void {
    this.inner.observe(fn as any);
  }

  observeDeep(fn: (events: Y.YEvent<any>[], transaction: Y.Transaction) => void): void {
    this.inner.observeDeep(fn);
  }

  unobserve(fn: (event: Y.YMapEvent<any>, transaction: Y.Transaction) => void): void {
    this.inner.unobserve(fn as any);
  }

  unobserveDeep(fn: (events: Y.YEvent<any>[], transaction: Y.Transaction) => void): void {
    this.inner.unobserveDeep(fn);
  }

  /**
   * Access the underlying Y.Map for cases that need direct Yjs access
   * (e.g., passing to observe handlers that check event.target).
   */
  get raw(): Y.Map<any> {
    return this.inner as Y.Map<any>;
  }

  // ── Private ──────────────────────────────────────────────────────────

  private snapshotFromYMap(id: string, ymap: Y.Map<unknown>): Record<string, any> {
    // Always build a fresh object so consumers get a new reference on every
    // read.  This is critical for reference-based change detection in
    // useDiagramRealtimeWithProjection and Zustand subscriptions.
    const snapshot: Record<string, any> = {};
    for (const key of ymap.keys()) {
      snapshot[key] = ymap.get(key);
    }

    // Store a separate cache copy for diff comparison in diffUpdate().
    // We cache the *values* (same references the Y.Map holds) so that
    // diffUpdate can skip unchanged keys.
    const cacheEntry: Record<string, any> = {};
    for (const key of Object.keys(snapshot)) {
      cacheEntry[key] = snapshot[key];
    }
    this.cache.set(id, cacheEntry);

    return snapshot;
  }

  private diffUpdate(id: string, ymap: Y.Map<unknown>, value: Record<string, any>): void {
    const cached = this.cache.get(id);
    const valueKeys = new Set(Object.keys(value));

    for (const [k, v] of Object.entries(value)) {
      // Use shallowEqual for objects (style, props) so that spreads like
      // `{ ...existing, x: 100 }` correctly skip unchanged nested objects
      // even though they have new references.
      if (cached && shallowEqual(cached[k], v)) continue;

      if (v === undefined) {
        ymap.delete(k);
      } else {
        ymap.set(k, v);
      }
    }

    for (const existingKey of [...ymap.keys()]) {
      if (!valueKeys.has(existingKey)) {
        ymap.delete(existingKey);
      }
    }

    // Rebuild cache from the Y.Map's current state so that consecutive
    // set() calls without an intervening get() can still diff correctly.
    const newCache: Record<string, any> = {};
    for (const key of ymap.keys()) {
      newCache[key] = ymap.get(key);
    }
    this.cache.set(id, newCache);
  }
}

/**
 * Create a ShapeMapAdapter from a Y.Doc's named map.
 */
export function createShapeMapAdapter(doc: Y.Doc, name = 'shapes'): ShapeMapAdapter {
  return new ShapeMapAdapter(doc.getMap(name));
}
